import React, { useEffect, useMemo, useState } from 'react';
import DataTable from "react-data-table-component";
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import '../../wallet.css';
import '../../wallet-fixes.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getDriverApiBaseUrl } from '../../utils/utils';
import {
    formatMoney, formatInt, formatDateTime,
    Avatar, Pill, EmptyState, WalletSpinner,
    useWalletToasts, WalletToastStack, txnTypeMeta, TXN_TYPE_META, useIsMobile,
} from './walletShared';

export default function DriverWalletDetail({ driverId }) {
    // getDriverApiBaseUrl() strips the trailing /shypvdriverapp, so add it back.
    const API_BASE = `${getDriverApiBaseUrl()}/shypvdriverapp/`;
    const navigate = useNavigate();
    const { toasts, toast, dismiss } = useWalletToasts();
    const isMobile = useIsMobile();

    const [transactions, setTransactions] = useState([]);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Collect Cash modal state
    const [collectModalOpen, setCollectModalOpen] = useState(false);
    const [amountCollected, setAmountCollected] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Admin bearer token (sessionStorage 'usertoken')
    const authHeaders = () => {
        const token = sessionStorage.getItem('usertoken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    useEffect(() => {
        if (driverId) {
            fetchTransactions();
            fetchBalanceHeader();
        }
    }, [driverId]);

    // Fetch the driver's ledger transactions
    const fetchTransactions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${API_BASE}admin/wallet/${driverId}/transactions`,
                { params: { page: 0, size: 100 }, headers: authHeaders() }
            );

            if (response.data && response.data.success) {
                setTransactions(response.data.data || []);
            } else {
                setError('Failed to fetch wallet transactions');
            }
        } catch (err) {
            console.error('Error fetching wallet transactions:', err);
            setError('Error fetching wallet transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch this driver's current balance from the balances list for the header
    const fetchBalanceHeader = async () => {
        try {
            const response = await axios.get(`${API_BASE}admin/wallet/balances`, {
                headers: authHeaders(),
            });
            if (response.data && response.data.success) {
                const list = response.data.data || [];
                const match = list.find(b => String(b.driverId) === String(driverId));
                if (match) setBalance(match);
            }
        } catch (err) {
            console.error('Error fetching driver balance header:', err);
        }
    };

    const handleBack = () => {
        navigate('/orders/driver-wallets');
    };

    // ── Filter + search ──────────────────────────────────────────────────────
    const filteredTransactions = useMemo(() => {
        let rows = transactions;
        if (typeFilter !== 'all') rows = rows.filter(t => t.type === typeFilter);
        const term = searchTerm.trim().toLowerCase();
        if (term) {
            rows = rows.filter(t =>
                (t.description || '').toLowerCase().includes(term) ||
                (t.sourceReference || '').toLowerCase().includes(term)
            );
        }
        return rows;
    }, [transactions, typeFilter, searchTerm]);

    const typeCounts = useMemo(() => {
        const counts = {};
        transactions.forEach(t => { counts[t.type] = (counts[t.type] || 0) + 1; });
        return counts;
    }, [transactions]);

    // ── Export CSV ───────────────────────────────────────────────────────────
    const exportCsv = () => {
        if (!filteredTransactions.length) return;
        const headers = ['Date', 'Type', 'Amount', 'Currency', 'Balance After', 'Reference', 'Source Type', 'Description'];
        const escape = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
        const lines = [headers.join(',')];
        filteredTransactions.forEach(t => {
            lines.push([
                formatDateTime(t.createdAt), txnTypeMeta(t.type).label, t.amount, t.currency || '',
                t.balanceAfter, t.sourceReference || '', t.sourceType || '', t.description || '',
            ].map(escape).join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `driver-${driverId}-wallet-ledger.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ── Collect cash (from this driver, directly on the ledger page) ──────────
    const openCollectModal = () => {
        setAmountCollected(balance?.outstanding != null ? String(balance.outstanding) : '');
        setNotes('');
        setCollectModalOpen(true);
    };
    const closeCollectModal = () => {
        setCollectModalOpen(false);
        setAmountCollected('');
        setNotes('');
    };
    const handleConfirmCollect = async () => {
        const amount = parseFloat(amountCollected);
        if (isNaN(amount) || amount <= 0) { toast.error('Please enter a valid amount greater than 0.'); return; }
        setSubmitting(true);
        try {
            const response = await axios.post(
                `${API_BASE}admin/wallet/${driverId}/collect`,
                { amountCollected: amount, notes },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
            );
            if (response.data && response.data.success) {
                toast.success(`Collected ${formatMoney(amount, balance?.currency)} from ${balance?.driverName || 'driver'}.`);
                closeCollectModal();
                fetchTransactions();
                fetchBalanceHeader();
            } else {
                const msg = (response.data && response.data.errors && response.data.errors.join(', ')) || 'Failed to collect cash';
                toast.error(msg);
            }
        } catch (err) {
            console.error('Error collecting cash:', err);
            toast.error('Error collecting cash. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Ledger copy for the current balance
    const balanceCopy = () => {
        if (!balance || balance.balance == null) return 'No wallet activity yet';
        if (balance.balance < 0) return `Holding ${formatMoney(Math.abs(balance.balance), balance.currency)} to hand over`;
        if (balance.balance === 0) return 'Settled — nothing owed either way';
        return `Credit of ${formatMoney(balance.balance, balance.currency)}`;
    };

    const columns = [
        {
            name: 'Date', selector: row => row.createdAt || '', sortable: true, width: '160px',
            cell: row => <span style={{ color: '#6b7280', fontSize: 12.5 }}>{formatDateTime(row.createdAt)}</span>,
        },
        {
            name: 'Type', selector: row => row.type || '', sortable: true, minWidth: '190px',
            cell: row => {
                const meta = txnTypeMeta(row.type);
                return <Pill tone={meta.tone} icon={meta.icon}>{meta.label}</Pill>;
            },
        },
        {
            name: 'Amount', selector: row => row.amount, sortable: true, right: true,
            cell: row => <Pill tone={row.amount < 0 ? 'danger' : 'success'}>{formatMoney(row.amount, row.currency)}</Pill>,
        },
        {
            name: 'Balance After', selector: row => row.balanceAfter, sortable: true, right: true,
            cell: row => <span style={{ fontWeight: 600, color: '#374151' }}>{formatMoney(row.balanceAfter)}</span>,
        },
        {
            name: 'Reference', selector: row => row.sourceReference || '-', sortable: true, minWidth: '150px', omit: isMobile,
            cell: row => row.sourceReference
                ? <span className="w-id-chip">{row.sourceReference}{row.sourceType ? ` · ${row.sourceType}` : ''}</span>
                : <span style={{ color: '#9aa1ac' }}>—</span>,
        },
        {
            name: 'Description', selector: row => row.description || '-', sortable: true, grow: 2,
            cell: row => (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', textAlign: 'left', color: '#4b5563', fontSize: 13 }}>
                    {row.description || '-'}
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: { style: { backgroundColor: '#fafbfc', borderBottom: '1px solid #e7eaf0', minHeight: '46px' } },
        headCells: { style: { fontWeight: '700', fontSize: '11.5px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.4px' } },
        rows: { style: { minHeight: '54px', fontSize: '13.5px', '&:hover': { backgroundColor: '#f7fafd' } } },
        pagination: { style: { borderTop: '1px solid #e7eaf0', fontSize: '12.5px', color: '#6b7280' } },
    };

    const canCollect = balance && Number(balance.outstanding) > 0;

    return (
        <div className="wallet-page wui">
            <WalletToastStack toasts={toasts} onDismiss={dismiss} />

            <button className="w-crumb" onClick={handleBack}>
                <i className="fa fa-arrow-left" aria-hidden="true" /> Back to Driver Wallets
            </button>

            {/* Driver summary card */}
            <div className="w-driver-card">
                <div className="w-person">
                    <Avatar name={balance?.driverName || `Driver #${driverId}`} size={52} />
                    <div>
                        <div className="w-title" style={{ fontSize: 17 }}>{balance?.driverName || `Driver #${driverId}`}</div>
                        <div className="w-person-sub" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                            <span className="w-id-chip">#{driverId}</span>
                            {balance?.phoneNumber && <span><i className="fa fa-phone" aria-hidden="true" /> {balance.phoneNumber}</span>}
                        </div>
                    </div>
                </div>
                <div className="w-driver-balance">
                    <div className="w-stat-label">Current Balance</div>
                    <div className="w-stat-value" style={{ color: !balance ? '#9aa1ac' : balance.balance < 0 ? '#e11d48' : '#16a34a' }}>
                        {balance ? formatMoney(balance.balance, balance.currency) : '—'}
                    </div>
                    <div className="w-stat-sub">{balanceCopy()}</div>
                    {canCollect && (
                        <button className="w-btn w-btn-success w-btn-sm" style={{ marginTop: 10 }} onClick={openCollectModal}>
                            <i className="fa fa-money" aria-hidden="true" /> Collect Cash
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="alert alert-danger"><i className="fa fa-exclamation-circle" aria-hidden="true" /> {error}</div>
            )}

            {/* Filters */}
            <div className="w-toolbar">
                <div className="w-chip-group">
                    <button className={`w-chip ${typeFilter === 'all' ? 'active' : ''}`} onClick={() => setTypeFilter('all')}>
                        All <span style={{ opacity: .7 }}>({formatInt(transactions.length)})</span>
                    </button>
                    {Object.keys(TXN_TYPE_META).map(type => typeCounts[type] ? (
                        <button key={type} className={`w-chip ${typeFilter === type ? 'active' : ''}`} onClick={() => setTypeFilter(type)}>
                            <i className={`fa ${TXN_TYPE_META[type].icon}`} aria-hidden="true" /> {TXN_TYPE_META[type].label} <span style={{ opacity: .7 }}>({typeCounts[type]})</span>
                        </button>
                    ) : null)}
                </div>
                <div className="w-input-wrap">
                    <i className="fa fa-search" aria-hidden="true" />
                    <input placeholder="Search description or reference…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <button className="w-btn w-btn-ghost w-btn-sm" onClick={exportCsv} disabled={!filteredTransactions.length} title="Export visible rows to CSV">
                    <i className="fa fa-download" aria-hidden="true" /> Export
                </button>
            </div>

            {/* DataTable */}
            <div className="w-card">
                <div style={{ overflowX: 'auto' }}>
                    <DataTable
                        columns={columns}
                        data={filteredTransactions}
                        pagination
                        paginationPerPage={25}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        progressPending={loading}
                        progressComponent={<WalletSpinner label="Loading transactions…" />}
                        noDataComponent={<EmptyState icon="fa-book" title="No transactions found" subtitle={transactions.length ? 'Try clearing your filters.' : 'This driver has no wallet activity yet.'} />}
                        customStyles={customStyles}
                        highlightOnHover
                        pointerOnHover
                    />
                </div>
            </div>

            {/* Collect Cash Modal */}
            {collectModalOpen && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content">
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-money" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>Collect Cash</h3>
                                <p>From {balance?.driverName || 'driver'}{balance?.phoneNumber ? ` · ${balance.phoneNumber}` : ''}</p>
                            </div>
                            <button className="close-btn" onClick={closeCollectModal}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div className="w-summary-line">
                                <span>Outstanding</span>
                                <strong style={{ color: '#e11d48' }}>{formatMoney(balance?.outstanding, balance?.currency)}</strong>
                            </div>
                            <div className="form-group">
                                <div className="w-field-row">
                                    <label style={{ margin: 0 }}>Amount Collected</label>
                                    <button type="button" className="w-link-btn"
                                        onClick={() => setAmountCollected(balance?.outstanding != null ? String(balance.outstanding) : '')}>
                                        Use full amount
                                    </button>
                                </div>
                                <div className="w-amount-input">
                                    <span>{balance?.currency || 'AED'}</span>
                                    <input type="number" min="0" step="0.01" value={amountCollected}
                                        onChange={(e) => setAmountCollected(e.target.value)}
                                        placeholder="0.00" />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Notes</label>
                                <textarea rows="3" value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Optional notes about this collection" />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={closeCollectModal} disabled={submitting}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={handleConfirmCollect} disabled={submitting || !amountCollected}>
                                {submitting ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Collecting…</> : <><i className="fa fa-check" aria-hidden="true" /> Confirm</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
